// World-space UI (decision 0046: "name plates, speech bubbles, enemy bars, damage numbers and interaction markers
// anchored to the world; hidden when occluded; off-screen markers clamp to the screen edge"), and the screen-edge
// indicators for off-screen attackers (decision 0063). A DOM overlay: each anchor is one pooled element placed by a
// CSS transform on drawn frames, written only when it moves or its text changes (the HUD's write discipline, no
// Preact render). The game passes the projection (its camera) and the occlusion test (its ray) as functions, so the
// package needs neither three.js nor a physics world. `placeOnScreen` is the pure placement rule.
import { UI_FAULTS } from "./faults";

/** A world point: [x, y, z] metres. */
export type WorldPos = readonly [number, number, number];

/** A point as the game's camera sees it: x and y in normalised device coordinates (-1..1, y up) and the distance in
 *  front of the camera (negative behind it; three.js mirrors x and y for such points, as most projections do). */
export interface Projected {
  x: number;
  y: number;
  depth: number;
}

/** Where a point lands on a screen of `width` × `height` px. */
export interface Placement {
  x: number;
  y: number;
  /** Inside the inset rectangle and in front of the camera. */
  onScreen: boolean;
  /** Moved to the inset rectangle's edge (off the screen, or behind the camera). */
  clamped: boolean;
  /** The direction from the screen's centre toward the point, radians (0 right, π/2 down): an edge arrow's angle. */
  angle: number;
}

/** Place a projected point: where it is when it is on the screen, else on the edge of the rectangle inset by `inset`
 *  px, along the line from the centre toward it (a point behind the camera counts as below the screen when it is
 *  straight behind). */
export function placeOnScreen(p: Projected, width: number, height: number, inset = 0): Placement {
  const cx = width / 2;
  const cy = height / 2;
  let dx = p.x * cx;
  let dy = -p.y * cy;
  const behind = p.depth <= 0;
  if (behind) {
    dx = -dx;
    dy = -dy;
  }
  const hx = Math.max(1, cx - inset);
  const hy = Math.max(1, cy - inset);
  const inside = !behind && Math.abs(dx) <= hx && Math.abs(dy) <= hy;
  if (inside) return { x: cx + dx, y: cy + dy, onScreen: true, clamped: false, angle: Math.atan2(dy, dx) };
  if (behind && Math.hypot(dx, dy) < 1e-3) dy = 1;
  const t = Math.min(Math.abs(dx) > 1e-9 ? hx / Math.abs(dx) : Infinity, Math.abs(dy) > 1e-9 ? hy / Math.abs(dy) : Infinity);
  const k = behind ? t : Math.min(1, t);
  return { x: cx + dx * k, y: cy + dy * k, onScreen: false, clamped: true, angle: Math.atan2(dy, dx) };
}

/** The kinds of anchor. */
export type AnchorKind = "plate" | "bubble" | "damage" | "marker" | "indicator";

/** What an anchor shows and how it behaves. */
export interface AnchorSpec {
  kind: AnchorKind;
  /** Its world point (read every drawn frame when a function). */
  pos: WorldPos | (() => WorldPos | null);
  /** A plate's name, a bubble's line, a damage number, a marker's label (already translated). */
  text?: string;
  /** A plate's bar, 0..1. */
  bar?: number;
  /** When a ray from the camera finds something in the way: hide it (default), dim it, or ignore it. */
  occlusion?: "hide" | "dim" | "none";
  /** Off the screen: clamp it to the edge with an arrow (markers, indicators), else hide it. Default false. */
  clamp?: boolean;
  /** Hidden farther than this from the camera (m). Default no limit. */
  maxDistance?: number;
  /** Seconds it lives (bubbles, damage numbers); removed after. Default forever. */
  life?: number;
  /** A styling hook (`data-tone`): "crit", "heal", "objective", "player"… */
  tone?: string;
  /** Damage numbers rise this many px over their life. Default 36. */
  rise?: number;
}

/** One anchor's last placement, for the game's handle and the specs. */
export interface AnchorState {
  id: string;
  kind: AnchorKind;
  text: string;
  shown: boolean;
  occluded: boolean;
  clamped: boolean;
  dimmed: boolean;
  x: number;
  y: number;
  angle: number;
  age: number;
}

/** Options for {@link createWorldUi}. */
export interface WorldUiOptions {
  /** Where the layer goes (default `document.body`). */
  container?: HTMLElement;
  /** The game's camera: a world point to normalised device coordinates and depth. */
  project(p: WorldPos): Projected;
  /** Is something between the camera and the point? (the game's ray; asked every `occlusionEvery` frames). */
  occluded(p: WorldPos): boolean;
  /** The screen's size, px (default the window's). */
  viewport?: () => { width: number; height: number };
  /** Clamped anchors stay this far inside the screen's edges, px (default 28; the safe areas add to it). */
  inset?: number;
  /** Frames between occlusion tests per anchor (staggered; default 4). */
  occlusionEvery?: number;
  /** For tests: the document to make elements in. */
  doc?: Document;
}

/** The world-space UI. */
export interface WorldUi {
  readonly root: HTMLElement;
  /** Add or update an anchor (its element is reused; text and bar are written only when they change). */
  set(id: string, spec: AnchorSpec): void;
  /** Add an anchor under a fresh id (damage numbers, bubbles); returns the id. */
  pop(spec: AnchorSpec): string;
  remove(id: string): void;
  has(id: string): boolean;
  /** Remove every anchor of a kind (or all). */
  clear(kind?: AnchorKind): void;
  /** Place everything for a drawn frame; `dt` (s) ages the timed anchors. */
  frame(dt: number): void;
  /** Show or hide the whole layer (screens over play hide it). */
  setVisible(on: boolean): void;
  /** Every anchor's last placement. */
  state(): AnchorState[];
  /** DOM writes so far (placements, texts, bars, visibility). */
  writes(): number;
  dispose(): void;
}

const CSS = `
.rpg-wui{position:fixed;inset:0;pointer-events:none;z-index:9;overflow:hidden;font-family:var(--rpg-font,system-ui,sans-serif)}
.rpg-wui[hidden]{display:none}
.rpg-wui-a{position:absolute;left:0;top:0;will-change:transform;color:#fbf6e8;text-shadow:0 1px 2px rgba(0,0,0,.8),0 0 4px rgba(0,0,0,.45);white-space:nowrap}
.rpg-wui-a[hidden]{display:none !important}
.rpg-wui-a[data-dim]{opacity:.45}
.rpg-wui-a[data-kind=plate]{display:flex;flex-direction:column;align-items:center;gap:2px;font:600 calc(12px*var(--rpg-text-scale,1))/1.1 var(--rpg-font,system-ui)}
.rpg-wui-bar{width:64px;height:6px;border-radius:3px;background:rgba(20,16,12,.7);box-shadow:0 0 0 1px rgba(0,0,0,.5);overflow:hidden}
.rpg-wui-bar>i{display:block;height:100%;background:#e0604a;transform-origin:left center}
.rpg-wui-a[data-kind=bubble]{max-width:min(46vw,320px);white-space:normal;padding:7px 11px;border-radius:12px;background:rgba(251,246,232,.96);color:#2b241b;text-shadow:none;
  font:500 calc(15px*var(--rpg-text-scale,1))/1.3 var(--rpg-font,system-ui);box-shadow:0 2px 8px rgba(0,0,0,.3)}
.rpg-wui-a[data-kind=damage]{font:800 calc(18px*var(--rpg-text-scale,1))/1 var(--rpg-font,system-ui);color:#fff3d6}
.rpg-wui-a[data-kind=damage][data-tone=crit]{color:#ffd447;font-size:calc(24px*var(--rpg-text-scale,1))}
.rpg-wui-a[data-kind=damage][data-tone=hurt]{color:#ff9a8a}
.rpg-wui-a[data-kind=marker],.rpg-wui-a[data-kind=indicator]{display:flex;flex-direction:column;align-items:center;font:700 calc(12px*var(--rpg-text-scale,1))/1.1 var(--rpg-font,system-ui)}
.rpg-wui-pin{width:16px;height:16px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);background:#e9c46a;box-shadow:0 0 0 2px rgba(29,42,31,.85)}
.rpg-wui-a[data-tone=poi] .rpg-wui-pin{background:#9fd8ff}
.rpg-wui-arrow{position:absolute;left:50%;top:50%;width:0;height:0;border-left:9px solid transparent;border-right:9px solid transparent;border-bottom:14px solid #e9c46a;
  margin:-7px 0 0 -9px;filter:drop-shadow(0 0 2px rgba(0,0,0,.7))}
.rpg-wui-a:not([data-clamped]) .rpg-wui-arrow{display:none}
.rpg-wui-a[data-kind=indicator] .rpg-wui-arrow{border-bottom-color:#ff5b3a;border-left-width:14px;border-right-width:14px;border-bottom-width:22px;margin:-11px 0 0 -14px}
`;

let styled = new WeakSet<Document>();
function injectWorldUiStyle(doc: Document): void {
  if (styled.has(doc) || !doc.head) return;
  styled.add(doc);
  const s = doc.createElement("style");
  s.dataset.rpgWorldUi = "";
  s.textContent = CSS;
  doc.head.appendChild(s);
}

interface Item {
  id: string;
  spec: AnchorSpec;
  el: HTMLElement;
  text: HTMLElement | null;
  bar: HTMLElement | null;
  arrow: HTMLElement | null;
  shownText: string | null;
  shownBar: number;
  age: number;
  seq: number;
  occluded: boolean;
  lastTest: number;
  st: AnchorState;
  px: number;
  py: number;
  vis: boolean | null;
  dim: boolean;
  clamped: boolean;
  angle: number;
  opacity: number;
}

/** Build the world-space UI layer. */
export function createWorldUi(o: WorldUiOptions): WorldUi {
  const doc = o.doc ?? o.container?.ownerDocument ?? document;
  injectWorldUiStyle(doc);
  const root = doc.createElement("div");
  root.className = "rpg-wui";
  root.setAttribute("aria-hidden", "true");
  root.dataset.worldUi = "";
  (o.container ?? doc.body).appendChild(root);
  const items = new Map<string, Item>();
  const every = Math.max(1, o.occlusionEvery ?? 4);
  const inset = o.inset ?? 28;
  const viewport = o.viewport ?? (() => ({ width: doc.defaultView?.innerWidth ?? 0, height: doc.defaultView?.innerHeight ?? 0 }));
  let frameNo = 0;
  let seq = 0;
  let writes = 0;
  let next = 1;

  const make = (id: string, spec: AnchorSpec): Item => {
    const el = doc.createElement("div");
    el.className = "rpg-wui-a";
    el.dataset.kind = spec.kind;
    el.dataset.anchor = id;
    el.setAttribute("hidden", "");
    let text: HTMLElement | null = null;
    let bar: HTMLElement | null = null;
    let arrow: HTMLElement | null = null;
    if (spec.kind === "marker" || spec.kind === "indicator") {
      arrow = doc.createElement("span");
      arrow.className = "rpg-wui-arrow";
      el.appendChild(arrow);
      if (spec.kind === "marker") {
        const pin = doc.createElement("span");
        pin.className = "rpg-wui-pin";
        el.appendChild(pin);
      }
    }
    if (spec.kind !== "indicator") {
      text = doc.createElement("span");
      text.className = "rpg-wui-text";
      el.appendChild(text);
    }
    if (spec.kind === "plate") {
      const b = doc.createElement("span");
      b.className = "rpg-wui-bar";
      bar = doc.createElement("i");
      b.appendChild(bar);
      el.appendChild(b);
    }
    root.appendChild(el);
    const st: AnchorState = { id, kind: spec.kind, text: "", shown: false, occluded: false, clamped: false, dimmed: false, x: 0, y: 0, angle: 0, age: 0 };
    return { id, spec, el, text, bar, arrow, shownText: null, shownBar: Number.NaN, age: 0, seq: seq++, occluded: false, lastTest: -Infinity, st, px: Number.NaN, py: Number.NaN, vis: null, dim: false, clamped: false, angle: Number.NaN, opacity: 1 };
  };

  const writeTexts = (it: Item): void => {
    const s = it.spec;
    const text = s.text ?? "";
    if (it.text && text !== it.shownText) {
      it.text.textContent = text;
      it.shownText = text;
      writes++;
    }
    if (it.bar) {
      const v = Math.min(1, Math.max(0, s.bar ?? 1));
      if (!(Math.abs(v - it.shownBar) < 0.002)) {
        it.bar.style.transform = `scaleX(${v})`;
        it.shownBar = v;
        writes++;
      }
    }
    if ((it.el.dataset.tone ?? "") !== (s.tone ?? "")) {
      if (s.tone) it.el.dataset.tone = s.tone;
      else delete it.el.dataset.tone;
      writes++;
    }
  };

  const show = (it: Item, on: boolean): void => {
    if (it.vis === on) return;
    it.vis = on;
    if (on) it.el.removeAttribute("hidden");
    else it.el.setAttribute("hidden", "");
    writes++;
  };

  const ui: WorldUi = {
    root,
    set(id, spec) {
      let it = items.get(id);
      if (it && it.spec.kind !== spec.kind) {
        it.el.remove();
        items.delete(id);
        it = undefined;
      }
      if (!it) {
        it = make(id, spec);
        items.set(id, it);
      }
      if (it.spec !== spec && spec.life !== undefined && it.spec.text !== spec.text) it.age = 0;
      it.spec = spec;
      writeTexts(it);
    },
    pop(spec) {
      const id = `${spec.kind}#${next++}`;
      ui.set(id, spec);
      return id;
    },
    remove(id) {
      const it = items.get(id);
      if (!it) return;
      it.el.remove();
      items.delete(id);
    },
    has: (id) => items.has(id),
    clear(kind) {
      for (const [id, it] of [...items]) if (!kind || it.spec.kind === kind) ui.remove(id);
    },
    frame(dt) {
      frameNo++;
      const { width, height } = viewport();
      for (const it of [...items.values()]) {
        const s = it.spec;
        it.age += dt;
        if (s.life !== undefined && it.age >= s.life) {
          ui.remove(it.id);
          continue;
        }
        writeTexts(it);
        const pos = typeof s.pos === "function" ? s.pos() : s.pos;
        if (!pos) {
          show(it, false);
          it.st = { ...it.st, shown: false, text: s.text ?? "", age: it.age };
          continue;
        }
        const pr = o.project(pos);
        const far = s.maxDistance !== undefined && pr.depth > s.maxDistance;
        let place = placeOnScreen(pr, width, height, s.clamp ? inset : 0);
        const clampable = !!s.clamp && !UI_FAULTS.worldUiNoClamp;
        // an indicator shows only while its point is off the screen
        let shown = !far && (place.onScreen || clampable);
        if (s.kind === "indicator") shown = !place.onScreen && !far;
        if (!place.onScreen && !clampable) place = { ...place, clamped: false };
        // occlusion: tested on screen only, every few frames, staggered (a damage number once, when it appears)
        const mode = s.occlusion ?? "hide";
        if (shown && place.onScreen && mode !== "none") {
          const due = s.kind === "damage" ? it.lastTest === -Infinity : (frameNo + it.seq) % every === 0 || it.lastTest === -Infinity;
          if (due) {
            it.occluded = o.occluded(pos);
            it.lastTest = frameNo;
          }
        } else if (!place.onScreen) it.occluded = false;
        const occluded = it.occluded && !UI_FAULTS.worldUiIgnoreOcclusion;
        if (occluded && mode === "hide") shown = false;
        const dim = occluded && mode === "dim";
        show(it, shown);
        if (shown) {
          let x = place.x;
          let y = place.y;
          let opacity = 1;
          if (s.kind === "damage" && s.life) {
            const k = it.age / s.life;
            y -= (s.rise ?? 36) * k;
            opacity = k < 0.6 ? 1 : Math.max(0, 1 - (k - 0.6) / 0.4);
          }
          // centred on the point; a plate, bubble or marker sits above it; a clamped one is centred on the edge point
          const lift = place.clamped ? "-50%" : s.kind === "damage" || s.kind === "indicator" ? "-50%" : "-100%";
          if (!(Math.abs(x - it.px) < 0.5 && Math.abs(y - it.py) < 0.5)) {
            it.el.style.transform = `translate3d(${x.toFixed(1)}px,${y.toFixed(1)}px,0) translate(-50%,${lift})`;
            it.px = x;
            it.py = y;
            writes++;
          }
          if (Math.abs(opacity - it.opacity) >= 0.02) {
            it.el.style.opacity = opacity.toFixed(2);
            it.opacity = opacity;
            writes++;
          }
          if (dim !== it.dim) {
            it.dim = dim;
            if (dim) it.el.setAttribute("data-dim", "");
            else it.el.removeAttribute("data-dim");
            writes++;
          }
          if (place.clamped !== it.clamped) {
            it.clamped = place.clamped;
            if (place.clamped) it.el.setAttribute("data-clamped", "");
            else it.el.removeAttribute("data-clamped");
            writes++;
          }
          if (it.arrow && place.clamped && !(Math.abs(place.angle - it.angle) <= 0.01)) {
            // the arrow is drawn pointing up: turn it toward the point
            it.arrow.style.transform = `rotate(${(place.angle + Math.PI / 2).toFixed(3)}rad) translateY(-22px)`;
            it.angle = place.angle;
            writes++;
          }
        }
        it.st = { id: it.id, kind: s.kind, text: s.text ?? "", shown, occluded, clamped: shown && place.clamped, dimmed: shown && dim, x: place.x, y: place.y, angle: place.angle, age: it.age };
      }
    },
    setVisible(on) {
      if (on) root.removeAttribute("hidden");
      else root.setAttribute("hidden", "");
    },
    state: () => [...items.values()].map((it) => ({ ...it.st })),
    writes: () => writes,
    dispose() {
      root.remove();
      items.clear();
    },
  };
  return ui;
}

/** For tests: forget which documents have the style (a fresh fake document each test). */
export function resetWorldUiStyle(): void {
  styled = new WeakSet();
}
